import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Flagship article (Educational): "What a digital twin actually is".
 * Honest, vendor-neutral body; product only appears in the closing block.
 * All numbers are illustrative.
 */
export const DIGITAL_TWIN_ARTICLE: Article = {
  slug: "what-a-digital-twin-actually-is",
  title: "What a digital twin actually is, for people who run plants",
  deck:
    "Strip away the marketing and a digital twin is a simple idea: a living model of your process that stays honest because it is fed by your real equipment. Here is the version without the hype.",
  goal: "Educational",
  readingMins: 7,
  heroStat: { value: "1:1", label: "model that mirrors the line you already run" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "If you have sat through a vendor pitch lately, you have probably been told that a digital twin will transform your operation. You have probably also left the room unsure what one actually is. That is fair — the term has been stretched to cover everything from a 3D animation to a full physics simulation. So let us be concrete. For someone who runs a plant, a digital twin is a structured, living model of your process that is continuously fed by data from your real equipment, so that what you see on screen reflects what is happening on the floor right now.",
    },
    {
      kind: "paragraph",
      text:
        "The keyword is living. A process flow diagram on the wall is a model too, but it was drawn once and it has been quietly drifting from reality ever since. A digital twin is the same map, except it is wired to the line: when a furnace is tapped, a mould is poured, or a specimen fails a test, the model knows. That connection — model plus live data plus history — is what separates a twin from a drawing.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The anatomy",
      heading: "Three things every real twin has",
      sub: "If a tool is missing one of these, it is a dashboard or a drawing — not a twin.",
    },
    {
      kind: "processFlow",
      title: "From floor to model",
      steps: [
        {
          title: "A model of the process",
          detail:
            "Your stages, equipment and the material that flows between them — represented as connected entities, not free-text notes.",
          icon: "gitBranch",
        },
        {
          title: "A live data feed",
          detail:
            "Sensor and instrument readings streamed in from the equipment that the model represents, tag by tag.",
          icon: "radio",
        },
        {
          title: "A memory",
          detail:
            "Every reading, batch and outcome stored over time, so you can ask 'what was different last Tuesday?' and get an answer.",
          icon: "database",
        },
      ],
      caption:
        "A twin is the combination, not any single piece. A model with no feed is a drawing; a feed with no model is a wall of numbers.",
    },
    {
      kind: "paragraph",
      text:
        "Why does the model matter so much? Because raw sensor data on its own is almost impossible to reason about. A tag called PT_204 reading 1432 means nothing until you know it is the pour temperature on line 2, that it sits downstream of the holding furnace, and that the mould it filled became casting batch C-8841. The model is what gives every number a place in the process — and once numbers have a place, you can follow cause and effect instead of guessing.",
    },
    {
      kind: "pullQuote",
      quote:
        "A drawing tells you how the plant was meant to work. A twin tells you how it is working — and remembers how it worked yesterday.",
      attribution: "A useful rule of thumb",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Why bother",
      heading: "What the model lets you do that a spreadsheet cannot",
    },
    {
      kind: "paragraph",
      text:
        "The payoff of a connected model is traceability and, on top of it, root-cause analysis. Because every output is tied to the inputs and conditions that produced it, you can walk a defective part backward to the exact heat, mould and process window behind it — and walk a suspect heat forward to every part it touched. That is the difference between a recall that quarantines a precise list of serials and one that scraps a whole week to be safe.",
    },
    {
      kind: "chart",
      chart: "line",
      title: "Illustrative: scrap rate before and after a connected model",
      xKey: "month",
      unit: "%",
      series: [{ dataKey: "scrap", label: "Scrap rate", tone: "danger" }],
      data: [
        { month: "M1", scrap: 6.4 },
        { month: "M2", scrap: 6.1 },
        { month: "M3", scrap: 5.7 },
        { month: "M4", scrap: 4.6 },
        { month: "M5", scrap: 4.0 },
        { month: "M6", scrap: 3.5 },
      ],
      caption:
        "Generic, illustrative figures — not a specific plant's results. The shape is the point: when causes become visible, the line tends to bend.",
    },
    {
      kind: "callout",
      tone: "info",
      title: "What a digital twin is not",
      text:
        "It is not necessarily a photo-real 3D scene, and it does not require a physics PhD to operate. A useful twin can be a clean process graph with live tags behind it. Fidelity should match the decision you are trying to make — no more.",
    },
    {
      kind: "paragraph",
      text:
        "There is also a quieter benefit: a twin becomes the shared language of the plant. The metallurgist, the maintenance lead and the plant manager finally look at the same model of the same process, with the same history underneath it. Arguments that used to be about whose spreadsheet was right become questions the data can answer.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Getting started",
      heading: "You do not boil the ocean",
    },
    {
      kind: "paragraph",
      text:
        "The biggest myth is that a twin is a multi-year, rip-and-replace program. In practice the sensible path is narrow and fast: model one line, connect the handful of tags that actually drive your worst defect, and start accumulating history. The model earns trust by being useful on one problem before it expands to the whole plant. Standards bodies have caught up here too — ISO 23247 frames exactly this kind of manufacturing twin — but you do not need a standards committee to start; you need one line and one question worth answering.",
    },
    {
      kind: "sahasEnables",
      intro:
        "A digital twin only earns its keep when the model, the live data and the history live in one place — and when building it does not require a six-month project. That is the part the platform handles.",
      capabilities: [
        {
          title: "Model your process visually",
          detail:
            "Lay out stages, equipment and the material flowing between them as a connected graph — or install a ready-wired plant template and adjust.",
          icon: "gitBranch",
        },
        {
          title: "Wire it to real tags",
          detail:
            "A self-hosted gateway streams live readings from your existing equipment into the model, so the twin reflects the floor.",
          icon: "radio",
        },
        {
          title: "Keep the memory",
          detail:
            "Every reading and batch is retained and tied to the model, so traceability and root-cause are queries, not archaeology.",
          icon: "database",
        },
      ],
    },
  ],
};
